import { useState, useEffect, useCallback } from 'react';
import { agentApi } from '../utils/agentApi';
import { Agent, OficialStatus } from '../types/agent';
import { useWhatsAppStatus } from './useWhatsAppStatus';

interface ConnectionStatus {
  whatsappNative: {
    isConnected: boolean;
    isLoading: boolean;
    isCheckingStatus: boolean;
    phoneNumber: string | null;
  };
  zapi: {
    isConnected: boolean;
    isLoading: boolean;
    isCheckingStatus: boolean;
    hasConfig: boolean;
    phoneNumber: string | null;
  };
  whatsappOficial: {
    isConnected: boolean;
    isLoading: boolean;
    isCheckingStatus: boolean;
    phoneNumber: string | null;
    status?: OficialStatus;
  };
}

interface UseConnectionStatusProps {
  agentId: string;
  agent?: Agent | null;
  onAgentUpdate?: (agent: Agent) => void;
}

export const useConnectionStatus = ({ agentId, agent, onAgentUpdate }: UseConnectionStatusProps) => {
  // Usar o hook original para WhatsApp nativo
  const whatsappStatus = useWhatsAppStatus({ 
    agentId, 
    checkInterval: agent?.whatsappConnected !== 'zapi' && agent?.whatsappConnected !== 'oficial' && agent?.whatsappOficialConfig == null ? 5 * 60 * 1000 : 0 
  });

  const [status, setStatus] = useState<ConnectionStatus>({
    whatsappNative: {
      isConnected: false,
      isLoading: false,
      isCheckingStatus: false,
      phoneNumber: null,
    },
    zapi: {
      isConnected: false,
      isLoading: false,
      isCheckingStatus: false,
      hasConfig: false,
      phoneNumber: null,
    },
    whatsappOficial: {
      isConnected: false,
      isLoading: false,
      isCheckingStatus: false,
      phoneNumber: null,
      status: undefined
    },
  });

  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error' | 'warning'>('success');

  // Função para mostrar snackbar
  const showSnackbar = useCallback((message: string, severity: 'success' | 'error' | 'warning') => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  }, []);

  // Verificar se Z-API tem configuração
  const hasZApiConfig = agent?.zApiConfig && 
    agent.zApiConfig.clientToken && 
    agent.zApiConfig.instanceId && 
    agent.zApiConfig.instanceToken;

  const hasWhatsAppOficialConfig = !!agent?.whatsappOficialConfig;

  const isWhatsAppOficialActive =
    status.whatsappOficial.isConnected ||
    (!!status.whatsappOficial.status && status.whatsappOficial.status !== 'disconnected') ||
    hasWhatsAppOficialConfig;

  // Sincronizar status do WhatsApp nativo com o hook original
  useEffect(() => {
    // Se WhatsApp Oficial está conectado, sempre desabilitar WhatsApp nativo
    if (agent?.whatsappConnected === 'oficial' && hasWhatsAppOficialConfig) {
      setStatus(prev => ({
        ...prev,
        whatsappNative: {
          isConnected: false,
          isLoading: false,
          isCheckingStatus: false,
          phoneNumber: null,
        },
      }));
      return;
    }
    
    // Se Z-API está conectado, também desabilitar WhatsApp nativo
    if (agent?.whatsappConnected === 'zapi') {
      return;
    }
    
    // Caso contrário, sincronizar com o hook original
    setStatus(prev => ({
      ...prev,
      whatsappNative: {
        isConnected: whatsappStatus.status.isConnected && whatsappStatus.status.loggedIn,
        isLoading: whatsappStatus.isLoading,
        isCheckingStatus: whatsappStatus.isCheckingStatus,
        phoneNumber: whatsappStatus.status.phoneNumber,
      },
    }));
  }, [whatsappStatus.status, whatsappStatus.isLoading, whatsappStatus.isCheckingStatus, agent?.whatsappConnected, hasWhatsAppOficialConfig]);

  // Verificar status do WhatsApp Oficial
  const checkWhatsAppOficialStatus = useCallback(async () => {
    if (!agentId) {
      setStatus(prev => ({
        ...prev,
        whatsappOficial: {
          isConnected: false,
          isLoading: false,
          isCheckingStatus: false,
          phoneNumber: null,
        },
      }));
      return;
    }

    setStatus(prev => ({ ...prev, whatsappOficial: { ...prev.whatsappOficial, isCheckingStatus: true } }));

    try {
      const whatsappStatus = await agentApi.getWhatsAppStatus(agentId);
      const isOficial = whatsappStatus.connectionType === 'oficial';
      const oficialStatus = whatsappStatus.whatsappOficialStatus;
      
      if (isOficial) {
        setStatus(prev => ({
          ...prev,
          whatsappOficial: {
            isConnected: !!(whatsappStatus.isConnected && whatsappStatus.isLoggedIn),
            isCheckingStatus: false,
            isLoading: false,
            phoneNumber: whatsappStatus.jid || null,
            status: oficialStatus as OficialStatus,
          },
        }));
      } else {
        setStatus(prev => ({
          ...prev,
          whatsappOficial: {
            isConnected: false,
            isCheckingStatus: false,
            isLoading: false,
            phoneNumber: null,
            status: undefined,
          },
        }));
      }
    } catch (error) {
      console.error('Erro ao verificar status WhatsApp Oficial:', error);
      // Em caso de erro, verificar se o agente está marcado como oficial
      if (agent?.whatsappConnected === 'oficial' && hasWhatsAppOficialConfig) {
        setStatus(prev => ({
          ...prev,
          whatsappOficial: {
            isConnected: true, // Assumir conectado se está marcado como oficial
            isCheckingStatus: false,
            isLoading: false,
            phoneNumber: null,
          },
        }));
      } else {
        setStatus(prev => ({
          ...prev,
          whatsappOficial: {
            isConnected: false,
            isCheckingStatus: false,
            isLoading: false,
            phoneNumber: null,
          },
        }));
      }
    }
  }, [agentId, agent?.whatsappConnected, hasWhatsAppOficialConfig]);

  // Conectar WhatsApp Oficial
  const connectWhatsAppOficial = useCallback(async () => {
    if (!agentId) return;

    setStatus(prev => ({ ...prev, whatsappOficial: { ...prev.whatsappOficial, isLoading: true } }));

    try {
      // Se outros WhatsApp estiverem conectados, desconectar primeiro
      if (status.whatsappNative.isConnected) {
        await whatsappStatus.disconnect();
        showSnackbar('WhatsApp nativo desconectado automaticamente - WhatsApp Oficial conectado', 'success');
      }
      if (status.zapi.isConnected && hasZApiConfig) {
        await agentApi.disconnectZApi(agentId);
        showSnackbar('Z-API desconectada automaticamente - WhatsApp Oficial conectado', 'success');
      }

      if (agent && onAgentUpdate) {
        const updatedAgent = { ...agent, whatsappConnected: 'oficial' as const };
        onAgentUpdate(updatedAgent);
      }

      await checkWhatsAppOficialStatus();
      showSnackbar('WhatsApp Oficial conectado', 'success');
    } catch (error) {
      console.error('Erro ao conectar WhatsApp Oficial:', error);
      showSnackbar('Erro ao conectar WhatsApp Oficial', 'error');
    } finally {
      setStatus(prev => ({ ...prev, whatsappOficial: { ...prev.whatsappOficial, isLoading: false } }));
    }
  }, [agentId, status.whatsappNative.isConnected, status.zapi.isConnected, hasZApiConfig, agent, onAgentUpdate, checkWhatsAppOficialStatus, whatsappStatus, showSnackbar]);

  const disconnectWhatsAppOficial = useCallback(async () => {
    if (!agentId) return;

    setStatus(prev => ({ ...prev, whatsappOficial: { ...prev.whatsappOficial, isLoading: true } }));

    try {
      await agentApi.disconnectWhatsAppOficial(agentId);
      showSnackbar('WhatsApp Oficial desconectado', 'success');
      
      setStatus(prev => ({
        ...prev,
        whatsappNative: {
          isConnected: false,
          isLoading: false,
          isCheckingStatus: false,
          phoneNumber: null,
        },
      }));

      // Buscar agente atualizado do backend
      try {
        const updatedAgent = await agentApi.getAgent(agentId);
        if (onAgentUpdate) {
          onAgentUpdate(updatedAgent);
        }
      } catch (error) {
        console.error('Erro ao buscar agente atualizado:', error);
        // Se falhar, atualizar localmente
        if (agent && onAgentUpdate) {
          const updatedAgent = { ...agent, whatsappConnected: undefined };
          onAgentUpdate(updatedAgent);
        }
      }

      await checkWhatsAppOficialStatus();
    } catch (error) {
      console.error('Erro ao desconectar WhatsApp Oficial:', error);
      showSnackbar('Erro ao desconectar WhatsApp Oficial', 'error');
    } finally {
      setStatus(prev => ({ ...prev, whatsappOficial: { ...prev.whatsappOficial, isLoading: false } }));
    }
  }, [agentId, agent, onAgentUpdate, checkWhatsAppOficialStatus, showSnackbar]);

  // Verificar status da Z-API
  const checkZApiStatus = useCallback(async () => {
    if (!agentId || !hasZApiConfig) return;

    setStatus(prev => ({ ...prev, zapi: { ...prev.zapi, isCheckingStatus: true } }));

    try {
      const zapiStatus = await agentApi.getZApiStatus(agentId);
      setStatus(prev => ({
        ...prev,
        zapi: {
          ...prev.zapi,
          isConnected: zapiStatus.connected,
          isCheckingStatus: false,
          phoneNumber: zapiStatus.connected ? (zapiStatus as any).phoneNumber || null : null,
        },
      }));
    } catch (error) {
      console.error('Erro ao verificar status Z-API:', error);
      setStatus(prev => ({
        ...prev,
        zapi: { ...prev.zapi, isCheckingStatus: false },
      }));
    }
  }, [agentId, hasZApiConfig]);

  // Conectar WhatsApp nativo usando o hook original
  const connectWhatsAppNative = useCallback(async () => {
    if (!agentId) return;

    try {
      // Se Z-API estiver conectada, desconectar primeiro
      if (status.zapi.isConnected && hasZApiConfig) {
        await agentApi.disconnectZApi(agentId);
        showSnackbar('Z-API desconectada automaticamente - WhatsApp nativo conectado', 'success');
      } else {
        showSnackbar('WhatsApp nativo conectado', 'success');
      }

      // Atualizar agente para refletir conexão nativa
      if (agent && onAgentUpdate) {
        const updatedAgent = { ...agent, whatsappConnected: 'native' as const };
        onAgentUpdate(updatedAgent);
      }

      // Usar o método do hook original
      await whatsappStatus.connect();
    } catch (error) {
      console.error('Erro ao conectar WhatsApp nativo:', error);
      showSnackbar('Erro ao conectar WhatsApp nativo', 'error');
    }
  }, [agentId, status.zapi.isConnected, hasZApiConfig, agent, onAgentUpdate, whatsappStatus, showSnackbar]);

  // Desconectar WhatsApp nativo usando o hook original
  const disconnectWhatsAppNative = useCallback(async () => {
    if (!agentId) return;

    try {
      // Usar o método do hook original
      await whatsappStatus.disconnect();
      showSnackbar('WhatsApp nativo desconectado', 'success');
      
      // Atualizar agente
      if (agent && onAgentUpdate) {
        const updatedAgent = { ...agent, whatsappConnected: undefined };
        onAgentUpdate(updatedAgent);
      }
    } catch (error) {
      console.error('Erro ao desconectar WhatsApp nativo:', error);
      showSnackbar('Erro ao desconectar WhatsApp nativo', 'error');
    }
  }, [agentId, agent, onAgentUpdate, whatsappStatus, showSnackbar]);

  // Conectar Z-API
  const connectZApi = useCallback(async () => {
    if (!agentId || !hasZApiConfig) return;

    setStatus(prev => ({ ...prev, zapi: { ...prev.zapi, isLoading: true } }));

    try {
      // Se WhatsApp nativo estiver conectado, desconectar primeiro
      if (status.whatsappNative.isConnected) {
        await whatsappStatus.disconnect();
        showSnackbar('WhatsApp nativo desconectado automaticamente - Z-API conectada', 'success');
      } else {
        showSnackbar('Z-API conectada', 'success');
      }

      // Atualizar agente para refletir conexão Z-API
      if (agent && onAgentUpdate) {
        const updatedAgent = { ...agent, whatsappConnected: 'zapi' as const };
        onAgentUpdate(updatedAgent);
      }

      await checkZApiStatus();
    } catch (error) {
      console.error('Erro ao conectar Z-API:', error);
      showSnackbar('Erro ao conectar Z-API', 'error');
    } finally {
      setStatus(prev => ({ ...prev, zapi: { ...prev.zapi, isLoading: false } }));
    }
  }, [agentId, hasZApiConfig, status.whatsappNative.isConnected, agent, onAgentUpdate, checkZApiStatus, whatsappStatus, showSnackbar]);

  // Desconectar Z-API
  const disconnectZApi = useCallback(async () => {
    if (!agentId) return;

    setStatus(prev => ({ ...prev, zapi: { ...prev.zapi, isLoading: true } }));

    try {
      await agentApi.disconnectZApi(agentId);
      showSnackbar('Z-API desconectada', 'success');
      
      // Atualizar agente
      if (agent && onAgentUpdate) {
        const updatedAgent = { ...agent, whatsappConnected: undefined };
        onAgentUpdate(updatedAgent);
      }

      await checkZApiStatus();
    } catch (error) {
      console.error('Erro ao desconectar Z-API:', error);
      showSnackbar('Erro ao desconectar Z-API', 'error');
    } finally {
      setStatus(prev => ({ ...prev, zapi: { ...prev.zapi, isLoading: false } }));
    }
  }, [agentId, agent, onAgentUpdate, checkZApiStatus, showSnackbar]);

  // Verificar status inicial da Z-API
  useEffect(() => {
    if (agent?.whatsappConnected === 'oficial' && hasWhatsAppOficialConfig) {
      // Se WhatsApp Oficial está conectado, desabilitar Z-API
      setStatus(prev => ({
        ...prev,
        zapi: {
          ...prev.zapi,
          isConnected: false,
        },
      }));
    } else if (agentId && hasZApiConfig) {
      checkZApiStatus();
    }
  }, [agentId, hasZApiConfig, checkZApiStatus, agent?.whatsappConnected, hasWhatsAppOficialConfig]);

  // Verificar status inicial do WhatsApp Oficial e sempre que o agente mudar
  useEffect(() => {
    if (agentId) {
      checkWhatsAppOficialStatus();
    }
  }, [agentId, agent?.whatsappConnected, agent?.whatsappOficialConfig, checkWhatsAppOficialStatus]);

  // Atualizar status da Z-API quando a configuração mudar
  useEffect(() => {
    setStatus(prev => ({
      ...prev,
      zapi: { ...prev.zapi, hasConfig: Boolean(hasZApiConfig) },
    }));
  }, [hasZApiConfig]);

  return {
    status,
    snackbarOpen,
    snackbarMessage,
    snackbarSeverity,
    setSnackbarOpen,
    connectWhatsAppNative,
    disconnectWhatsAppNative,
    connectZApi,
    disconnectZApi,
    connectWhatsAppOficial,
    disconnectWhatsAppOficial,
    checkZApiStatus,
    checkWhatsAppOficialStatus,
    whatsappStatus,
    hasWhatsAppOficialConfig,
    isWhatsAppOficialActive,
  };
};
